import React, { useEffect, useState } from "react";
import { FileText, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { fetchMembers } from "../../features/members/slice-extensions/members-slice-extension";
import { fetchPaymentTypes } from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import ContractForm from "./ContractForm";
import FormModal from "./FormModal";

interface BillingFirstContractCardProps {
  /** Called after the first contract was created, so the host can switch views. */
  onCreated: () => void;
}

/**
 * The "no contracts yet" first step, shared by the home page and the billing
 * page: one card, one primary action. The contract form itself covers the
 * truly fresh account — it creates the first payment type inline.
 */
const BillingFirstContractCard: React.FC<BillingFirstContractCardProps> = ({ onCreated }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [showForm, setShowForm] = useState(false);

  // The form's member and price selects feed from the store.
  useEffect(() => {
    dispatch(fetchMembers());
    dispatch(fetchPaymentTypes({ includeArchived: false }));
  }, [dispatch]);

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <h3 className="flex items-center gap-2 text-grey-10 dark:text-grey-90">
        <FileText size={20} className="text-grey-30 dark:text-grey-70" />
        {t("billing.overview.noContractsTitle")}
      </h3>
      <p className="mt-2 max-w-xl text-sm text-grey-40 dark:text-grey-60">
        {t("billing.overview.noContractsText")}
      </p>
      <button
        type="button"
        className="btn btn-primary mt-4"
        onClick={() => setShowForm(true)}
      >
        <Plus size={18} strokeWidth={2} />
        <span>{t("billing.actions.newContract")}</span>
      </button>

      {showForm && (
        <FormModal onClose={() => setShowForm(false)}>
          <ContractForm
            onSaved={() => {
              setShowForm(false);
              onCreated();
            }}
            onClose={() => setShowForm(false)}
          />
        </FormModal>
      )}
    </div>
  );
};

export default BillingFirstContractCard;
